import { vm } from "../socket/config.js";
import {
    targetToName,
    nameToTarget,
    setLocalFlag,
} from "../utils/scratch-helpers.js";
import { setPauseEventHandling } from "../vm/overrides.js";

export const onCostumeAdded = async (data) => {
    const msg = data.data;
    const { target: targetName, ret } = msg;

    const [costumeName, costumeData] = ret;
    const target = nameToTarget(targetName);
    if (!target) {
        console.error("Target not found", targetName);
        return;
    }

    setLocalFlag(true);
    setPauseEventHandling(true);
    try {
        const originalTarget = vm.editingTarget;

        let asset = costumeData.asset;
        const dataBuffer = Uint8Array.from(costumeData.assetData);

        asset = await vm.runtime.storage.createAsset(
            costumeData.asset.assetType,
            costumeData.asset.dataFormat,
            dataBuffer,
            costumeData.asset.assetId,
            true,
        );

        const costumeObj = {
            name: costumeData.name || costumeName,
            dataFormat: costumeData.dataFormat,
            asset: asset,
            md5:
                costumeData.md5 ||
                `${asset.assetId}.${costumeData.dataFormat}`,
            assetId: asset.assetId,
            ...(costumeData.bitmapResolution !== undefined && {
                bitmapResolution: costumeData.bitmapResolution,
            }),
            ...(costumeData.rotationCenterX !== undefined && {
                rotationCenterX: costumeData.rotationCenterX,
            }),
            ...(costumeData.rotationCenterY !== undefined && {
                rotationCenterY: costumeData.rotationCenterY,
            }),
        };

        await vm.addCostume(costumeName, costumeObj, target.id, undefined);
        vm.emitTargetsUpdate();

        if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
            vm.editingTarget = originalTarget;
            vm.runtime._editingTarget = originalTarget;
        }
    } catch (e) {
        console.error("Error adding remote costume:", e);
    } finally {
        setPauseEventHandling(false);
    }
};

export const onCostumeDelete = async (data) => {
    const msg = data.data;

    setLocalFlag(true);
    const originalTarget = vm.editingTarget;
    vm.editingTarget = nameToTarget(msg.target);

    await vm.deleteCostume(msg.costumeIndex);
    vm.emitTargetsUpdate();

    if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
        vm.editingTarget = originalTarget;
        vm.runtime._editingTarget = originalTarget;
    }
};

export const onCostumeReorder = (data) => {
    const { costumeIndex, newIndex, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    setLocalFlag(true);
    vm.reorderCostume(t.id, costumeIndex, newIndex);
    vm.emitTargetsUpdate();
};

export const onCostumeRename = (data) => {
    const { costumeIndex, name, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    const originalTarget = vm.editingTarget;
    setLocalFlag(true);
    vm.editingTarget = t;

    vm.renameCostume(costumeIndex, name);

    if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
        vm.editingTarget = originalTarget;
        vm.runtime._editingTarget = originalTarget;
    }
};

export const onCostumeDuplicate = async (data) => {
    const { costumeIndex, name, target } = data.data;
    const t = nameToTarget(target);

    if (!t) {
        console.error("Target not found", target);
        return;
    }

    const originalTarget = vm.editingTarget;
    vm.editingTarget = t;

    setLocalFlag(true);
    await vm.duplicateCostume(costumeIndex);

    const copy = t.sprite.costumes[costumeIndex + 1];
    if (copy && name) copy.name = name;

    if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
        vm.editingTarget = originalTarget;
        vm.runtime._editingTarget = originalTarget;
    }
};

export const onSvgUpdated = async (data) => {
    const { assetType, costume, costumeIdx, target, assetData } = data.data;
    const t = nameToTarget(target);
    const c = t?.getCostumes()[costumeIdx];
    if (!t || !c) {
        console.error("Target or costume not found", target, costumeIdx);
        return;
    }

    setPauseEventHandling(true);
    setLocalFlag(true);
    try {
        const originalTarget = vm.editingTarget;

        const dataBuffer = Uint8Array.from(assetData);
        const existingAsset = vm.runtime.storage.get(costume.assetId);

        if (existingAsset) {
            existingAsset.setData(dataBuffer, true);
            c.asset = existingAsset;
        } else {
            const resolvedAssetType =
                vm.runtime.storage.AssetType[assetType?.name] ||
                vm.runtime.storage.AssetType.ImageVector;

            c.asset = await vm.runtime.storage.createAsset(
                resolvedAssetType,
                costume.dataFormat,
                dataBuffer,
                costume.assetId,
                true,
            );
        }

        const { skinId: _remoteSvgSkinId, ...remoteSvgCostume } = costume;
        Object.assign(c, remoteSvgCostume);

        if (vm?.runtime?.renderer && c.skinId !== undefined) {
            const svg = new TextDecoder().decode(c.asset.data);
            vm.runtime.renderer.updateSVGSkin(c.skinId, svg, [
                c.rotationCenterX,
                c.rotationCenterY,
            ]);
        }

        let selectedCostumeIndex = getSelectedCostumeIndex();
        if (
            targetToName(vm.editingTarget) == target &&
            selectedCostumeIndex != -1 &&
            costumeIdx == selectedCostumeIndex
        ) {
            let cost = vm.editingTarget.getCostumes()[costumeIdx];
            let paper = getPaper();

            if (paper) {
                paper.switchCostume(
                    cost.dataFormat,
                    cost.asset.encodeDataURI(),
                    cost.rotationCenterX,
                    cost.rotationCenterY,
                    paper.props.zoomLevelId,
                    paper.props.zoomLevelId,
                );
            }
        }

        t.updateAllDrawableProperties();

        if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
            vm.editingTarget = originalTarget;
            vm.runtime._editingTarget = originalTarget;
        }
    } catch (e) {
        console.error("Error applying remote SVG update:", e);
    } finally {
        setPauseEventHandling(false);
    }
};

export const onBitmapUpdate = async (data) => {
    const {
        costume,
        target,
        costumeIndex,
        assetType,
        assetData,
        h,
        w,
        bitmapResolution,
    } = data.data;

    const t = nameToTarget(target);
    const c = t?.getCostumes()[costumeIndex];
    if (!t || !c) {
        console.error("Target or costume not found", target, costumeIndex);
        return;
    }

    setPauseEventHandling(true);
    setLocalFlag(true);
    try {
        const originalTarget = vm.editingTarget;
        const dataBuffer = assetData ? Uint8Array.from(assetData) : undefined;

        const existingAsset = vm.runtime.storage.get(costume.assetId);

        if (existingAsset) {
            existingAsset.setData(dataBuffer, true);
            c.asset = existingAsset;
        } else {
            const resolvedAssetType =
                vm.runtime.storage.AssetType[assetType?.name] ||
                vm.runtime.storage.AssetType.ImageBitmap;

            c.asset = await vm.runtime.storage.createAsset(
                resolvedAssetType,
                costume.dataFormat || "png",
                dataBuffer,
                costume.assetId,
                true,
            );
        }

        c.bitmapResolution = bitmapResolution || costume.bitmapResolution || 2;
        c.size = costume.size || [w, h];
        c.rotationCenterX = costume.rotationCenterX ?? w / 2;
        c.rotationCenterY = costume.rotationCenterY ?? h / 2;

        const { skinId: _remoteBitmapSkinId, ...remoteBitmapCostume } =
            costume;
        Object.assign(c, remoteBitmapCostume);

        if (vm?.runtime?.renderer && c.skinId !== undefined && dataBuffer) {
            const decodedBitmap = await createImageBitmap(
                new Blob([dataBuffer], {
                    type: `image/${costume.dataFormat || "png"}`,
                }),
            );

            vm.runtime.renderer.updateBitmapSkin(
                c.skinId,
                decodedBitmap,
                c.bitmapResolution,
                [
                    c.rotationCenterX / c.bitmapResolution,
                    c.rotationCenterY / c.bitmapResolution,
                ],
            );
        }

        let selectedCostumeIndex = getSelectedCostumeIndex();
        if (
            targetToName(vm.editingTarget) == target &&
            selectedCostumeIndex != -1 &&
            costumeIndex == selectedCostumeIndex
        ) {
            let cost = vm.editingTarget.getCostumes()[costumeIndex];
            let paper = getPaper();

            if (paper) {
                paper.switchCostume(
                    cost.dataFormat,
                    cost.asset.encodeDataURI(),
                    cost.rotationCenterX,
                    cost.rotationCenterY,
                    paper.props.zoomLevelId,
                    paper.props.zoomLevelId,
                );
            }
        }

        t.updateAllDrawableProperties();

        if (originalTarget && vm.runtime.getTargetById(originalTarget.id)) {
            vm.editingTarget = originalTarget;
            vm.runtime._editingTarget = originalTarget;
        }
    } catch (e) {
        console.error("Error applying remote bitmap update:", e);
    } finally {
        setPauseEventHandling(false);
    }
};

const getSelectedCostumeIndex = () => {
    const item = document.querySelector(
        "[class*='selector_list-item'][class*='sprite-selector-item_is-selected']",
    );
    if (!item) return -1;
    const numberEl = item.querySelector(
        "[class*='sprite-selector-item_number']",
    );
    if (!numberEl) return -1;
    return +numberEl.textContent - 1;
};

function getPaper() {
    let paperContainer = document.querySelector(
        "[class^='paint-editor_canvas-container']",
    );
    if (!paperContainer) return null;
    let reactInst;
    for (let e of Object.entries(paperContainer)) {
        if (e[0].startsWith("__reactInternalInstance")) {
            reactInst = e[1];
            break;
        }
    }
    return reactInst?.child?.child?.child?.stateNode;
}
